import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface TemplateVariable {
  key: string;
  label: string;
  description: string;
  required: boolean;
  defaultValue?: string;
  twilioVariable?: string;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  description: string;
  twilioContentSid: string;
  category: 'notification' | 'marketing' | 'utility';
  language: string;
  variables: TemplateVariable[];
  previewTemplate: string;
  status: 'approved' | 'pending' | 'rejected';
  usageScenarios: string[];
  usedBy?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TemplateManager: React.FC<Props> = ({ open, onClose }) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showTemplateDetails, setShowTemplateDetails] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Load templates when component opens
  useEffect(() => {
    if (open && templates.length === 0) {
      loadTemplates();
    }
  }, [open, templates.length]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: WhatsAppTemplate['category']) => {
    const colors: Record<WhatsAppTemplate['category'], string> = {
      notification: 'primary',
      utility: 'secondary',
      marketing: 'success'
    };
    return colors[category] || 'default';
  };

  const getStatusColor = (status: WhatsAppTemplate['status']) => {
    const colors: Record<WhatsAppTemplate['status'], string> = {
      approved: 'success',
      pending: 'warning',
      rejected: 'error'
    };
    return colors[status] || 'default';
  };

  const handleRowExpand = (templateId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(templateId)) {
      newExpanded.delete(templateId);
    } else {
      newExpanded.add(templateId);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateDetails(true);
  };

  const renderVariablesList = (variables: TemplateVariable[]) => (
    <List dense>
      {variables.map((variable, index) => (
        <ListItem key={index} divider>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {`{{${variable.key}}}`}
                </Typography>
                {variable.required && (
                  <Chip label="必填" size="small" color="error" variant="outlined" />
                )}
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="text.primary">
                  {variable.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {variable.description}
                </Typography>
                {variable.defaultValue && (
                  <Typography variant="caption" color="text.secondary">
                    預設值: {variable.defaultValue}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  const renderTemplatePreview = (template: WhatsAppTemplate) => (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        bgcolor: '#f5f5f5', 
        fontFamily: 'monospace',
        whiteSpace: 'pre-line',
        fontSize: '0.9rem'
      }}
    >
      {template.previewTemplate}
    </Paper>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            WhatsApp 訊息模板管理
          </Box>
        </DialogTitle>
        
              <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          這些模板與您的 Twilio WhatsApp 模板配置對應。修改模板需要同時更新 Twilio 的模板設定。
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="200">模板名稱</TableCell>
                  <TableCell width="120">分類</TableCell>
                  <TableCell width="100">狀態</TableCell>
                  <TableCell width="200">Twilio Content SID</TableCell>
                  <TableCell width="150">使用場景</TableCell>
                  <TableCell width="120">操作</TableCell>
                  <TableCell width="50"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <React.Fragment key={template.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {template.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {template.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={template.category} 
                          size="small" 
                          color={getCategoryColor(template.category) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={template.status} 
                          size="small" 
                          color={getStatusColor(template.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {template.twilioContentSid}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {template.usageScenarios.slice(0, 2).map((scenario: string, index: number) => (
                            <Chip 
                              key={index}
                              label={scenario}
                              size="small" 
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                          {template.usageScenarios.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{template.usageScenarios.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewTemplate(template)}
                          title="查看詳情"
                        >
                          <PreviewIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRowExpand(template.id)}
                        >
                          {expandedRows.has(template.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={expandedRows.has(template.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              模板變數 ({template.variables.length})
                            </Typography>
                            {renderVariablesList(template.variables)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* Template Details Dialog */}
      <Dialog 
        open={showTemplateDetails} 
        onClose={() => setShowTemplateDetails(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate?.name} - 模板詳情
        </DialogTitle>
        
        <DialogContent>
          {selectedTemplate && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      基本資訊
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        模板ID
                      </Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {selectedTemplate.id}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Twilio Content SID
                      </Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {selectedTemplate.twilioContentSid}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        分類
                      </Typography>
                      <Chip 
                        label={selectedTemplate.category} 
                        size="small" 
                        color={getCategoryColor(selectedTemplate.category) as any}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        狀態
                      </Typography>
                      <Chip 
                        label={selectedTemplate.status} 
                        size="small" 
                        color={getStatusColor(selectedTemplate.status) as any}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        語言
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.language}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      使用場景
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedTemplate.usageScenarios.map((scenario: string, index: number) => (
                        <Chip 
                          key={index}
                          label={scenario} 
                          size="small" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      模板變數
                    </Typography>
                    {renderVariablesList(selectedTemplate.variables)}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      模板預覽
                    </Typography>
                    {renderTemplatePreview(selectedTemplate)}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowTemplateDetails(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateManager; 